/**
 * Gibt zurück, ob Element1 und Element2 gleich sind (im Sinne von Pdf-Bestellseiten)
 */
import { equalsEpsilon } from "oc-commons/Maths";
import * as Scenes from "oc-threejs/Scenes";
import { ausrichtungCode } from "../components/TorEigenschaften";
import { PerspectiveCamera, Quaternion } from "three";
import { ZElement } from "./ZElement";
import { SnapshotRenderer } from "oc-threejs/SnapshotRenderer";
import { v3 } from "oc-threejs/short3";
import {
  createSchiebetorSkizzeBlob,
  createSchiebetorFundamentBlobs,
  berechneFundament1Überstand,
  berechneSchiebetorFundamente,
} from "../arrangement3d/schiebetor/schiebetorSkizze";
import { schiebetorKennzahlen, erzeugeSchiebetorRahmen } from "../arrangement3d/schiebetor/erzeugeSchiebetorRahmen";
import { MassRuler } from "../torkonfigurator/MassRuler";
import { BoxRuler } from "../arrangement3d/BoxRuler";
import { getGehflügel, getStandflügel } from "./elementFunctions";
import { Flügel } from "./Flügel";
import { Jaco3d } from "oc-jaco3d/arrangement3d/Jaco3d";

let ausrichtungBilderMap = {};
let massElementSnapshotQueue: Promise<void> = Promise.resolve();

function runSnapshotTaskSerial<T>(task: () => Promise<T>): Promise<T> {
  const nextTask = massElementSnapshotQueue.then(task, task);
  massElementSnapshotQueue = nextTask.then(
    () => undefined,
    () => undefined
  );
  return nextTask;
}
/**
 * Lädt während der Kompilierung alle Ausrichtungsbilder in diese Map, damit sie dynamisch genutzt werden können
 */
function fillAusrichtungBilderMap() {
  const requireContext = require.context("../style/ausrichtung_bilder", false);
  ausrichtungBilderMap = {};
  requireContext.keys().forEach((item, index) => {
    ausrichtungBilderMap[item.replace("./", "").replace(".png", "").replace(".jpg", "")] = requireContext(item);
  });
}
fillAusrichtungBilderMap();

function addSeite1Seite2LabelsToAusrichtungBlob(blob: Blob): Promise<Blob> {
  return new Promise((resolve) => {
    const imageUrl = URL.createObjectURL(blob);
    const image = new Image();

    image.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = image.width;
      canvas.height = image.height;

      const context = canvas.getContext("2d");
      if (!context) {
        URL.revokeObjectURL(imageUrl);
        resolve(blob);
        return;
      }

      context.drawImage(image, 0, 0, canvas.width, canvas.height);
      context.font = `${Math.max(16, Math.round(canvas.height * 0.105))}px Arial`;
      context.fillStyle = "#000000";
      context.textBaseline = "top";

      const leftPadding = Math.max(10, Math.round(canvas.width * 0.04));
      const topPadding = Math.max(8, Math.round(canvas.height * 0.04));
      const bottomPadding = Math.max(8, Math.round(canvas.height * 0.04));

      context.textAlign = "left";
      context.fillText(I18N("Seite 1"), leftPadding, topPadding);

      context.textBaseline = "bottom";
      context.fillText(I18N("Seite 2"), leftPadding, canvas.height - bottomPadding);

      canvas.toBlob((resultBlob) => {
        URL.revokeObjectURL(imageUrl);
        resolve(resultBlob ?? blob);
      }, "image/png");
    };

    image.onerror = () => {
      URL.revokeObjectURL(imageUrl);
      resolve(blob);
    };

    image.src = imageUrl;
  });
}

export function renderBlobOnWhiteBackground(blob: Blob): Promise<Blob> {
  return new Promise((resolve) => {
    const imageUrl = URL.createObjectURL(blob);
    const image = new Image();

    image.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = image.width;
      canvas.height = image.height;

      const context = canvas.getContext("2d");
      if (!context) {
        URL.revokeObjectURL(imageUrl);
        resolve(blob);
        return;
      }

      context.fillStyle = "#ffffff";
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.drawImage(image, 0, 0, canvas.width, canvas.height);

      canvas.toBlob((resultBlob) => {
        URL.revokeObjectURL(imageUrl);
        resolve(resultBlob ?? blob);
      }, "image/png");
    };

    image.onerror = () => {
      URL.revokeObjectURL(imageUrl);
      resolve(blob);
    };

    image.src = imageUrl;
  });
}

/**
 * Gehe MassElemente, MassTore und MassProfile durch und lade gegebenenfalls Bilder für
 * Ausrichtung und DetailBild hoch. Finde Duplikate und markiere diese mit isDuplicate
 */
export async function generierePdfBestellseitenBilderUndAnzahlen(
  configurationJson,
  additionalCanvasBlobs
) {
  const massElementeJson = [];
  const tasks: Promise<void>[] = [];
  configurationJson.segmente.forEach((segmentJson) => {
    segmentJson.partsAndPfosten.forEach((partJson) => {
      const alreadyAddedMassElementJson = massElementeJson.find((elementJson) =>
        isEqualMassElement(elementJson, partJson)
      );
      if (alreadyAddedMassElementJson) {
        alreadyAddedMassElementJson.anzahl = alreadyAddedMassElementJson.anzahl
          ? alreadyAddedMassElementJson.anzahl + 1
          : 2;
        partJson.isDuplicate = true;
      } else {
        massElementeJson.push(partJson);
        if (
          partJson.isMasstor ||
          partJson.isMasselement ||
          partJson.isMassProfil ||
          partJson.isBestellseite
        ) {
          tasks.push(generateUploadsForMassElement(partJson, additionalCanvasBlobs));
        }
      }
    });
  });
  await Promise.all(tasks);
}

function isEqualMassElement(elementJson1, elementJson2): boolean {
  if (elementJson1.isMassProfil && elementJson2.isMassProfil) {
    return (
      elementJson1.artikelID === elementJson2.artikelID &&
      equalsEpsilon(elementJson1.breite, elementJson2.breite)
    );
  } else if (elementJson1.isMasselement && elementJson2.isMasselement) {
    return (
      elementJson1.artikelID === elementJson2.artikelID &&
      equalsEpsilon(elementJson1.breite, elementJson2.breite) &&
      equalsEpsilon(elementJson1.höhe, elementJson2.höhe) &&
      elementJson1.füllungAuswahlwert === elementJson2.füllungAuswahlwert
    );
  } else {
    return false;
  }
}

/**
 * Hier werden für ein Element das Ausrichtungsbild rausgesucht und hochgeladen und das
 * Detailbild mit der Füllungsseite gerendert. Zusätzlich wird ein 2D-Snapshot erzeugt.
 * Alles wird in this.additionalCanvasBlobs gepackt,
 * damit es hochgeladen wird. Die Dateinamen sind
 * ausrichtung-[elementNr].png, detailBild-[elementNr].png und snapshot2d-[elementNr].png
 * @param element
 */
async function generateUploadsForMassElement(elementJson, additionalCanvasBlobs): Promise<void> {
  if (elementJson.elementNr == null) return;
  const element = APP.arrangement.getElemente()[elementJson.elementNr];
  const tasks: Promise<void>[] = [];

  if (element.isMasstor || (element.isTor && element.isBestellseite)) {
    const ac = ausrichtungCode(element);
    const ausrichtungBildUrl = ausrichtungBilderMap[ac];
    if (ausrichtungBildUrl) {
      tasks.push(
        fetch(ausrichtungBildUrl)
          .then((response) => response.blob())
          .then((blob) => {
            additionalCanvasBlobs.push({
              fileName: "ausrichtung-" + elementJson.elementNr + ".png",
              blob,
            });
          })
          .catch((error) => {
            console.warn("Konnte Ausrichtungsbild nicht erzeugen", elementJson?.elementNr, error);
          })
      );
    } else {
      console.warn("Kein Ausrichtungbild mit Code " + ac + " in Map für Part ", element);
    }
  }

  if (
    element.isMasstor ||
    element.isMasselement ||
    element.isMassProfil ||
    (element.isTor && element.isBestellseite)
  ) {
    const snapshot2dBlobPromise = getMassElement2dBlobPromise(element);
    if (snapshot2dBlobPromise) {
      const snapshot2dFileName = "snapshot2d-" + elementJson.elementNr + ".png";
      elementJson.show2dSnapshot = true;
      elementJson.snapshot2dFileName = snapshot2dFileName;
      tasks.push(
        snapshot2dBlobPromise
          .then((blob) => {
            if (!blob) {
              console.warn(
                "2D-Snapshot ist leer und wird im PDF ausgelassen",
                elementJson?.elementNr
              );
              return;
            }
            additionalCanvasBlobs.push({
              fileName: snapshot2dFileName,
              blob,
            });
          })
          .catch((error) => {
            console.warn("Konnte 2D-Snapshot nicht erzeugen", elementJson?.elementNr, error);
          })
      );
    }
  }

  const detailBildBlobPromise = getDetailBildBlobPromise(element);
  if (detailBildBlobPromise) {
    elementJson.showFüllungsseite = true;
    elementJson.showDetailBild = true;
    tasks.push(
      detailBildBlobPromise
        .then((blob) => {
          additionalCanvasBlobs.push({
            fileName: "detailBild-" + elementJson.elementNr + ".png",
            blob,
          });
        })
        .catch((error) => {
          console.warn("Konnte Detailbild nicht erzeugen", elementJson?.elementNr, error);
        })
    );
  }

  if (element?.isSchiebetor) {
    elementJson.showSchiebetorSkizze = true;
    elementJson.schiebetorPuffer = APP.settings.SchiebetorPuffer ?? 0.2;
    elementJson.fundamentÜberstand = berechneFundament1Überstand(element);
    elementJson.platzbedarfTiefe = berechneSchiebetorFundamente(element)?.fundament2.tiefeBrut;
    // Kennzahlen für die PHP-Tabelle im PDF mitgeben
    const skK = schiebetorKennzahlen({ lichteWeite: element.lichteWeite, torHöhe: element.size.y });
    const skRahmen = erzeugeSchiebetorRahmen({
      torHöhe: element.size.y,
      lichteWeite: element.lichteWeite,
      schiebetorFlügelMaxBreite: element.meta?.Mass?.SchiebetorFlügelMaxBreite ?? 1.86,
      gespiegeltZ: element.schiebetormotorseiteAuswahlwert?.Key === "Schiebetormotorseite_B",
    });
    const eFensterBreite = skRahmen.füllungsFenster?.[0]
      ? Math.round((skRahmen.füllungsFenster[0].max.x - skRahmen.füllungsFenster[0].min.x) * 1000)
      : null;
    elementJson.schiebetorKennzahlen = {
      A: Math.round(skK.A * 1000),
      B: Math.round(skK.B * 1000),
      C: Math.round(skK.C * 1000),
      D: Math.round(skK.D * 1000),
      E: eFensterBreite,
      F: Math.round((skK.F * 180) / Math.PI),
      G: Math.round(skK.G * 1000),
    };
    const schiebetorSkizzeFileName = "schiebetorSkizze-" + elementJson.elementNr + ".png";
    elementJson.schiebetorSkizzeFileName = schiebetorSkizzeFileName;
    tasks.push(
      createSchiebetorSkizzeBlob(element)
        .then((blob) => {
          additionalCanvasBlobs.push({
            fileName: schiebetorSkizzeFileName,
            blob,
          });
        })
        .catch((err) => {
          console.warn("Schiebetor-Skizze konnte nicht erzeugt werden", err, elementJson);
        })
    );

    // Fundamentplan-Bilder (Seitenansicht + Draufsicht)
    elementJson.showSchiebetorFundament = true;
    const fundamentSideFileName = "schiebetorFundamentSide-" + elementJson.elementNr + ".png";
    const fundamentTopFileName = "schiebetorFundamentTop-" + elementJson.elementNr + ".png";
    elementJson.schiebetorFundamentSideFileName = fundamentSideFileName;
    elementJson.schiebetorFundamentTopFileName = fundamentTopFileName;
    tasks.push(
      createSchiebetorFundamentBlobs(element)
        .then(({ seitenansicht, draufsicht }) => {
          additionalCanvasBlobs.push({
            fileName: fundamentSideFileName,
            blob: seitenansicht,
          });
          additionalCanvasBlobs.push({
            fileName: fundamentTopFileName,
            blob: draufsicht,
          });
        })
        .catch((err) => {
          console.warn("Schiebetor-Fundamentplan konnte nicht erzeugt werden", err, elementJson);
        })
    );
  }

  await Promise.all(tasks);
}

/**
 * Blendet für Snapshot-Renderings störende Szenenobjekte aus:
 * Hover-/SelectBoxen, Warnungen, Aufbauten, Kabel und Empty-Objekte.
 * Gibt ein Objekt mit reapply() (zum erneuten Ausblenden nach update3d) und restore() zurück.
 */
function hideSceneVisualizersForSnapshot(element: ZElement) {
  const partSelectionVisualizers = APP.arrangement
    .getPartsAndPfosten()
    .map((part) => part?.jaco3d)
    .filter((part3d) => part3d != null)
    .map((part3d) => ({
      hoverBox: (part3d as any).hoverBox,
      hoverVisible: (part3d as any).hoverBox?.visible,
      selectBox: (part3d as any).selectBox,
      selectVisible: (part3d as any).selectBox?.visible,
    }));
  const warningVisualizers = (APP.arrangement.warnungen ?? [])
    .map((warnung) => warnung?.jaco3d)
    .filter((warnung3d) => warnung3d != null)
    .map((warnung3d) => ({ warnung3d, visible: warnung3d.visible }));
  const aufbautenVisualizers = (APP.arrangement.aufbauten ?? [])
    .filter(
      (aufbau) =>
        !element.anMauer ||
        !APP.arrangement.ecken.some(
          (ecke) =>
            ecke.anMauer &&
            ecke.segmente?.includes(element.segment) &&
            ecke.aufbauten.includes(aufbau)
        )
    )
    .map((aufbau) => aufbau?.jaco3d)
    .filter((aufbau3d) => aufbau3d != null)
    .map((aufbau3d) => ({ aufbau3d, visible: aufbau3d.visible }));
  const kabelVisualizers = (APP.arrangement.kabel ?? [])
    .map((kabel) => kabel?.jaco3d)
    .filter((kabel3d) => kabel3d != null)
    .map((kabel3d) => ({ kabel3d, visible: kabel3d.visible }));
  const emptyObjects = Scenes.filter(APP.scene, (obj) => obj?.name === "Empty")
    .map((obj) => ({ obj, visible: obj.visible }));

  const hide = () => {
    partSelectionVisualizers.forEach(({ hoverBox, selectBox }) => {
      hoverBox && (hoverBox.visible = false);
      selectBox && (selectBox.visible = false);
    });
    warningVisualizers.forEach(({ warnung3d }) => {
      warnung3d.visible = false;
    });
    aufbautenVisualizers.forEach(({ aufbau3d }) => {
      aufbau3d.visible = false;
    });
    kabelVisualizers.forEach(({ kabel3d }) => {
      kabel3d.visible = false;
    });
    emptyObjects.forEach(({ obj }) => {
      obj.visible = false;
    });
  };

  hide();

  return {
    reapply: hide,
    restore() {
      partSelectionVisualizers.forEach(({ hoverBox, hoverVisible, selectBox, selectVisible }) => {
        hoverBox && (hoverBox.visible = hoverVisible);
        selectBox && (selectBox.visible = selectVisible);
      });
      warningVisualizers.forEach(({ warnung3d, visible }) => {
        warnung3d.visible = visible;
      });
      aufbautenVisualizers.forEach(({ aufbau3d, visible }) => {
        aufbau3d.visible = visible;
      });
      kabelVisualizers.forEach(({ kabel3d, visible }) => {
        kabel3d.visible = visible;
      });
      emptyObjects.forEach(({ obj, visible }) => {
        obj.visible = visible;
      });
    },
  };
}

/**
 * Sucht bei gegebenem Element nach dem Detailbild auf Klobenseite.
 * Bei Doppeltoren wird das Detailbild gesucht, welches näher am Pfosten ist.
 * Bei Schiebetoren wird das Detailbild gesucht, welches näher am Motor ist.
 * Auf dieses Detailbild wird gezoomt und der Ausschnitt gerendert.
 * Zurückgegeben wird das Promise, welches das Blob-Objekt ausspuckt.
 * Falls das Element gar kein Detailbild hat, so wird null zurückgegeben
 * @param element
 * @param distance
 * @param width
 * @param height
 * @returns {Promise | null}
 */
function getDetailBildBlobPromise(element: ZElement, distance = 0.5, width = 320, height = 200) {
  if (!element || !element.jaco3d) {
    console.warn(
      "Versucht ein DetailBild zu rendern ohne ein Element anzugeben! So kann ich nicht arbeiten!"
    );
    return null;
  }

  const alleDetailBilder = Scenes.filter(element.jaco3d, (child) => child.name == "DetailBild");
  if (alleDetailBilder.length < 1) {
    return null;
  }

  return runSnapshotTaskSerial(async () => {
    // DetailBilder erneut suchen, da update3dPlain() im vorherigen Task die Szene neu aufgebaut haben kann
    const detailBilder = Scenes.filter(element.jaco3d, (child) => child.name == "DetailBild");
    if (detailBilder.length < 1) {
      return null;
    }

    const visualizers = hideSceneVisualizersForSnapshot(element);

    let detailBild = detailBilder[0];

    // Motormodell und dessen Weltposition für Schiebetore ermitteln
    const motorModel = element.isSchiebetor
      ? element.schiebetorFixPunkt?.["Schiebetor_EAntrieb_Came"] ??
        element.schiebetorFixPunkt?.["Schiebetor_EAntrieb_Unterbau"]
      : null;
    const motorWorldPos = motorModel?.getWorldPosition(v3(0, 0, 0));

    if (element.isSchiebetor && motorWorldPos && detailBilder.length > 1) {
      // Bei Schiebetoren: DetailBild wählen, das am nächsten am Motor liegt
      let minDist = Infinity;
      for (const db of detailBilder) {
        const dist = motorWorldPos.distanceTo(db.posboxWorld.center);
        if (dist < minDist) {
          minDist = dist;
          detailBild = db;
        }
      }
    } else if (detailBilder.length > 1) {
      // Bei Doppeltoren suche das DetailBild, welches näher am Kloben ist (also weiter weg vom Zentrum)
      const partMidX = element.jaco3d.posboxWorld.center.x;
      const x0 = detailBilder[0].posboxWorld.center.x;
      const x1 = detailBilder[1].posboxWorld.center.x;
      if (Math.abs(x0 - partMidX) < Math.abs(x1 - partMidX)) detailBild = detailBilder[1];
    }

    const detailBildPosition = detailBild.posboxWorld.center;

    let lookAtTarget = detailBildPosition;
    let cameraDistance = distance;

    if (element.isSchiebetor && motorWorldPos) {
      // Kamera so positionieren, dass Motor und DetailBild-Lupe beide sichtbar sind
      lookAtTarget = detailBildPosition.clone().add(motorWorldPos).multiplyScalar(0.5);
      const separation = detailBildPosition.distanceTo(motorWorldPos);
      // Abstand berechnen, damit beide Objekte ins Sichtfeld der Kamera passen
      const hFovHalf = Math.atan(Math.tan((30 * Math.PI) / 180) * (width / height));
      const requiredDist = separation / 2 / Math.tan(hFovHalf);
      cameraDistance = Math.max(distance, requiredDist * 1.8);
    }

    const camera = new PerspectiveCamera(60, width / height, 0.1, 1000);
    camera.position.copy(lookAtTarget);
    const lookDirection = v3(0, 0, 1)
      .normalize()
      .clone()
      .applyQuaternion(element.parent.getWorldQuaternion());

    if (element.isSchiebetor && motorWorldPos) {
      // Kamerarichtung anhand der Motorseite bestimmen
      const motorseiteA = element.schiebetormotorseiteAuswahlwert.Key == "Schiebetormotorseite_A";
      camera.position.add(
        lookDirection.clone().multiplyScalar(motorseiteA ? -cameraDistance : cameraDistance)
      );
    } else {
      camera.position.add(
        lookDirection.multiplyScalar(element.isÖffnungNachInnen ? -cameraDistance : cameraDistance)
      );
    }

    camera.lookAt(lookAtTarget);
    const renderer = new SnapshotRenderer();
    renderer.scene = APP.scene;
    renderer.camera = camera;
    renderer.setSize(width, height);

    try {
      const blob = await renderer.getBlobPromise("image/png");
      return blob ? renderBlobOnWhiteBackground(blob) : blob;
    } finally {
      visualizers.restore();
    }
  });
}

function getMassElement2dBlobPromise(
  element: ZElement,
  width = 1200,
  height = 800
): Promise<Blob | null> | null {
  if (!element || !element.jaco3d) {
    console.warn("Kein 2D-Snapshot möglich ohne gültiges Element", element);
    return null;
  }

  if (APP.isTorkonfigurator) {
    APP.previewPictures?.resetView();
  }

  return runSnapshotTaskSerial(async () => {
    const previousCameraConfig = STAGE.getCurrentCameraConfig();
    const visualizers = hideSceneVisualizersForSnapshot(element);

    // Breitenmaße als BoxRuler für Tore einblenden.
    // Werden absichtlich vor setOrthographicMode hinzugefügt, damit die Kamera sie automatisch einrahmt.
    const massRuler: Array<{ parentJaco: Jaco3d; ruler: BoxRuler }> = [];
    const mr = new MassRuler();
    const addBreitenRuler = (
      part: ZElement | Flügel,
      description: object,
      breiteOverride?: number
    ) => {
      if (!part?.jaco3d || typeof part.getBox !== "function") return;
      const ruler = new BoxRuler();
      (ruler as any).description = description;
      const partBox = part.getBox().clone();
      if (typeof breiteOverride === "number" && isFinite(breiteOverride) && breiteOverride > 0) {
        const centerX = (partBox.min.x + partBox.max.x) / 2;
        const halfWidth = breiteOverride / 2;
        partBox.min.x = centerX - halfWidth;
        partBox.max.x = centerX + halfWidth;
      }
      ruler.updateBox(partBox);
      part.jaco3d.add(ruler);
      massRuler.push({ parentJaco: part.jaco3d, ruler });
    };
    if (element.isDoppeltor) {
      addBreitenRuler(getStandflügel(element), {
        xtop: mr.descriptions.standflügelbreite,
      });
      addBreitenRuler(getGehflügel(element), {
        xtop: mr.descriptions.gehflügelbreite,
      });
    }
    addBreitenRuler(element, { xtop: mr.descriptions.lichteweite }, element.lichteWeite);

    const orthographicBorder = element.isSchiebetor ? 0.01 : 0.15;

    // Zoom-Richtung an die Weltausrichtung des Elements anpassen
    const elementWorldQuat = element.jaco3d.getWorldQuaternion(new Quaternion());
    const worldZoomDir = v3(0, 0, -0.001).applyQuaternion(elementWorldQuat);

    try {
      STAGE.setOrthographicMode(true, {
        object: element.jaco3d,
        zoomDirection: worldZoomDir,
        up: v3(0, 1, 0),
        border: orthographicBorder,
      });

      // zweimal update3d, um Material-/Darstellungszustände zu stabilisieren.
      APP.update3dPlain();
      APP.update3dPlain();

      visualizers.reapply();

      const directPfosten = (element.getPfosten?.() ?? []).filter((pfosten) => pfosten != null);
      const visibleFocusParts = new Set([element, ...directPfosten]);
      const partsAndPfostenVisibility = APP.arrangement
        .getPartsAndPfosten()
        .filter((part) => part?.jaco3d != null)
        .map((part) => ({ part, visible: part.jaco3d.visible }));
      partsAndPfostenVisibility.forEach(({ part }) => {
        part.jaco3d.visible = visibleFocusParts.has(part);
      });

      const previousRenderSize = STAGE.getRenderSize();
      const renderWidth = element.isSchiebetor ? 1200 : width;
      const renderHeight = element.isSchiebetor ? 600 : height;
      STAGE.setRendererToSize(renderWidth, renderHeight);

      const temporaryLengthRulerSet = new Set(
        massRuler.flatMap(({ ruler }) =>
          Object.values(((ruler as any).objects ?? {}) as Record<string, any>).filter(
            (obj) => obj != null
          )
        )
      );

      const hiddenObjectsByName = [
        APP.scene.getObjectByName("sky"),
        APP.scene.getObjectByName("ground"),
        APP.scene.getObjectByName("groundRaster"),
        APP.scene.getObjectByName("grundrissShape"),
      ].filter((obj) => obj != null);
      const hiddenBackgroundObjects = Scenes.filter(
        APP.scene,
        (obj) => (obj as any).isSceneBackground === true
      );
      const hiddenLengthRulers = Scenes.filter(
        APP.scene,
        (obj) => obj?.name === "LengthRuler" && !temporaryLengthRulerSet.has(obj)
      );
      const hiddenObjectSet = new Set([
        ...hiddenObjectsByName,
        ...hiddenBackgroundObjects,
        ...hiddenLengthRulers,
      ]);
      const hiddenObjects = [...hiddenObjectSet].map((obj) => ({ obj, visible: obj.visible }));

      hiddenObjects.forEach(({ obj }) => {
        obj.visible = false;
      });

      // Bei Orthographic-Kamera left/right auf Ziel-Seitenverhältnis mappen,
      // damit keine Verzerrung entsteht.
      if ((STAGE.camera as any).isOrthographicCamera) {
        const orthographicZoomFactor = element.isSchiebetor ? 1.65 : 1;
        const halfHeight = (STAGE.camera as any).top / orthographicZoomFactor;
        const targetAspect = renderWidth / renderHeight;
        (STAGE.camera as any).top = halfHeight;
        (STAGE.camera as any).bottom = -halfHeight;
        (STAGE.camera as any).left = -halfHeight * targetAspect;
        (STAGE.camera as any).right = halfHeight * targetAspect;
        (STAGE.camera as any).updateProjectionMatrix();
      }
      try {
        const rawBlob = await fetch(STAGE.renderToCanvas().toDataURL("image/png"))
          .then((response) => response.blob())
          .catch(() => null);
        if (!rawBlob) {
          return null;
        }
        return await renderBlobOnWhiteBackground(rawBlob);
      } finally {
        STAGE.setRendererToSize(previousRenderSize.width, previousRenderSize.height);
        partsAndPfostenVisibility.forEach(({ part, visible }) => {
          part.jaco3d.visible = visible;
        });
        hiddenObjects.forEach(({ obj, visible }) => {
          obj.visible = visible;
        });
      }
    } finally {
      massRuler.forEach(({ parentJaco, ruler }) => parentJaco.remove(ruler));
      STAGE.setCameraConfig(previousCameraConfig);
      visualizers.restore();
    }
  });
}
